import type { DashboardCardType, DashboardLayoutZone } from "../../../../types/dashboardLayout"
import {
  dashboardCardRegistry,
  dashboardCardTypesInDefaultOrder,
  DASHBOARD_MAX_MINI_STAT_SLOTS,
} from "./dashboardCardRegistry"
import { DASHBOARD_SPLITTABLE_CHART_ROW_CARD_TYPES } from "./dashboardChartRows"
import {
  isDashboardMiniStatDuplicateOfVisible,
  isDashboardMiniStatPickerCard,
} from "./dashboardLayoutCardPolicy"

export interface GetDashboardLayoutSelectableCardsInput {
  zone: DashboardLayoutZone
  replaceCardSlot?: DashboardCardType | null
  usedVisibleCardTypes: ReadonlySet<DashboardCardType>
  visibleMiniStatCount: number
  fullWidthAddAnchor?: "belowTradeDistribution" | null
}

/** Cards the user can pick for a layout zone (add or replace), matching placement rules. */
export function getDashboardLayoutSelectableCards({
  zone,
  replaceCardSlot = null,
  usedVisibleCardTypes,
  visibleMiniStatCount,
  fullWidthAddAnchor = null,
}: GetDashboardLayoutSelectableCardsInput): DashboardCardType[] {
  return dashboardCardTypesInDefaultOrder.filter((cardType) => {
    const entry = dashboardCardRegistry[cardType]
    if (!entry.compatibleZones.includes(zone)) return false
    // Non-removable cards stay off the "add" picker, but can still be chosen when
    // replacing within their zone (e.g. swap Milestones back to Portfolio Growth).
    if (!entry.removable && !replaceCardSlot) return false
    if (
      zone === "miniStats" &&
      !replaceCardSlot &&
      visibleMiniStatCount >= DASHBOARD_MAX_MINI_STAT_SLOTS
    ) {
      return false
    }
    if (zone === "fullWidth" && fullWidthAddAnchor === "belowTradeDistribution") {
      if (!DASHBOARD_SPLITTABLE_CHART_ROW_CARD_TYPES.includes(cardType)) return false
    }
    if (zone === "miniStats") {
      if (!isDashboardMiniStatPickerCard(cardType)) return false
      if (isDashboardMiniStatDuplicateOfVisible(cardType, usedVisibleCardTypes)) return false
    }
    if (replaceCardSlot && cardType === replaceCardSlot) return true
    return !usedVisibleCardTypes.has(cardType)
  })
}

/** Swap targets for an occupied slot (excludes the widget already in the slot). */
export function getDashboardLayoutReplaceOptions(
  input: GetDashboardLayoutSelectableCardsInput & { slotCardType: DashboardCardType }
): DashboardCardType[] {
  const splittable = DASHBOARD_SPLITTABLE_CHART_ROW_CARD_TYPES.includes(input.slotCardType)
  return getDashboardLayoutSelectableCards({
    ...input,
    replaceCardSlot: input.slotCardType,
  }).filter((cardType) => {
    if (cardType === input.slotCardType) return false
    if (splittable) {
      return DASHBOARD_SPLITTABLE_CHART_ROW_CARD_TYPES.includes(cardType)
    }
    return true
  })
}
